import { named, num, text, toggle, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { look, onPhone, pad, space, paint, stack } from './styleTokens';
import { COLOR_SCHEMES } from '../render/colorSchemes';

// The four boxes everything else sits in. They carry almost no content of their own — on a band, the
// tag it should render as, and otherwise nothing — because a box is edited through its Styles tab, and
// the id a link elsewhere on the page reaches it by is the Custom Id every element already carries. What they do carry is `accepts`, and that is the rule the whole drag experience
// rests on: a row takes only columns, and a column takes anything at all.

// LightFunnels' gutter is a fixed 20px rather than a step of a scale. Every column of theirs is spelled
// `calc(99.99% * (n/12) - (20px - 20px * (n/12)))`, and that arithmetic only adds back up to a full line
// when the row's own gap is exactly that gutter — `shareWidth` in `presets/presetBox.ts` writes the same
// 20px into the widths. A hand-dropped row reaching for `spacing.md` — 28px to 40px depending on the
// theme — put the two halves of the sum out of step, which is the last cell of a strip wrapping.
const LF_GUTTER = '20px';

/**
 * How wide the content of a band runs — LightFunnels' *Layout · Type*, the one control every page of
 * theirs opens on. A band is always the full width of the window; the question is whether what sits
 * inside it runs edge to edge or is held to a column down the middle, and theirs holds it to 1210px.
 * Without it a merchant could only centre a band's content by setting a max width on the row inside it
 * and remembering to do it again on the next one.
 */
const CONTENT_WIDTHS: readonly Choice[] = [['full', 'Full width'], ['boxed', 'Boxed']];

/**
 * Whether a band stays put as the page scrolls — Shopify's "Header sticky" setting, offered on every
 * band because a promise bar wants it as much as a header does. A setting rather than a position
 * style, because "on scroll up" is a behaviour: the band hides while the shopper reads down and comes
 * back the moment they scroll up, which no stylesheet can express.
 */
const STICKY: readonly Choice[] = [['none', 'No'], ['always', 'Always'], ['scroll-up', 'On scroll up']];

/** The landmarks a band may render as, under the words a merchant reads rather than the tags. */
const BAND_TAGS: readonly Choice[] = [
  ['section', 'Section'], ['header', 'Header'], ['footer', 'Footer'], ['main', 'Main content'], ['aside', 'Sidebar']
];

/** A full-width band: the unit a page is still read as, even now that its insides are the merchant's. */
export const sectionElement = element('section', {
  label: 'Section',
  description: 'A full-width band across the page — the unit a visitor reads a page in.',
  category: 'Layouts',
  icon: 'layout-panel-top',
  props: [
    named('layout', 'Content width', CONTENT_WIDTHS),
    { ...num('contentWidth', 'Boxed width', 320, 2400), help: 'How wide the column runs while the content width is Boxed.' },
    named('tag', 'Renders as', BAND_TAGS),
    named('sticky', 'Sticky', STICKY),
    { ...named('scheme', 'Colour scheme', COLOR_SCHEMES), help: 'Soft and Dark are built from Section background and Dark background under Theme settings.' }
  ],
  defaults: {
    // 1210px is LightFunnels' own boxed width, kept so a page built to their measurements lands the same.
    props: { layout: 'full', contentWidth: 1210, tag: 'section', sticky: 'none', scheme: '' },
    styles: onPhone(
      look({ display: 'block', width: '100%', backgroundColor: paint('surface'), ...pad(space('xl'), space('md')) }),
      pad(space('lg'), space('sm'))
    ),
    // A band lands holding the grid it is read in, which is what LightFunnels drops: a section, a row
    // inside it, a column inside that, with the column's empty slot as the invitation. An empty band was
    // a dead rectangle — the merchant's next element landed beside the row rather than in it.
    children: [{ type: 'row' }]
  },
});

/** A horizontal band of columns, which folds to one column per line on a phone. */
export const rowElement = element('row', {
  label: 'Row',
  description: 'A line of columns side by side on a screen, stacked one above the other on a phone.',
  category: 'Layouts',
  icon: 'rows-3',
  props: [],
  defaults: {
    props: {},
    styles: onPhone(
      look({
        display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: LF_GUTTER,
        width: '100%', maxWidth: '1440px', marginLeft: 'auto', marginRight: 'auto'
      }),
      { flexDirection: 'column' }
    ),
    // A row accepts columns and nothing else, so an empty one refuses every card in the library. It
    // lands holding the one child it can hold, and the merchant drops into that.
    children: [{ type: 'column' }]
  },
});

/** One column of a row: the box that takes anything, and the one a merchant drops into most. */
export const columnElement = element('column', {
  label: 'Column',
  description: 'One column inside a row. It takes any element, and shares the row’s width with its neighbours.',
  category: 'Layouts',
  icon: 'columns-3',
  props: [{ ...toggle('foldOnPhone', 'Fold under its heading on phones'), help: 'On a phone the first element heads the column and the rest open under a chevron, as a footer’s menus do.' }],
  defaults: {
    props: {},
    styles: onPhone(
      look({ ...stack(space('sm')), alignItems: 'center', flexGrow: 1, flexShrink: 1, minWidth: '0px' }),
      { width: '100%' }
    )
  },
});

/** A box of elements that opens as a dialog a few seconds after a shopper arrives — a newsletter popup. */
export const popupElement = element('popup', {
  label: 'Popup',
  description: 'A box that opens over the page a few seconds after a shopper arrives, then not again for some days.',
  category: 'Layouts',
  icon: 'message-square',
  props: [
    { ...toggle('enabled', 'Show it to shoppers'), help: 'Off, it is only drawn here in the editor.' },
    { ...num('delay', 'Seconds before it opens', 0, 60), help: 'Counted from when the page loads.' },
    { ...num('againAfter', 'Days before it opens again', 1, 90), help: 'For a shopper who closed it.' },
    { ...text('label', 'Name for screen readers'), help: 'Read out as the dialog opens, e.g. Newsletter.' }
  ],
  defaults: {
    props: { enabled: false, delay: 5, againAfter: 7, label: 'Newsletter' },
    styles: look({ display: 'flex', width: '100%', backgroundColor: paint('surface') })
  }
});

/** Whether a box's children slide sideways as cards: never, at every width, or on a phone alone. */
const SLIDES: readonly Choice[] = [['off', 'No'], ['always', 'Always'], ['phone', 'On phones only']];

/** Where a sliding box's two arrows sit; on a phone a swipe is the way through either way. */
export const SLIDER_ARROWS: readonly Choice[] = [
  ['above', 'Above the cards'], ['heading', 'Level with the heading above'], ['none', 'None, swipe only']
];

/** A centred content box — what a group of elements is wrapped in so it reads as a column, not full bleed. */
export const containerElement = element('container', {
  label: 'Container',
  description: 'A plain box you group elements inside, so a background, a border or a padding covers them all.',
  category: 'Other',
  icon: 'box',
  props: [
    { ...named('slider', 'Slide its contents', SLIDES), help: 'Each element inside becomes a card a shopper swipes, or steps through with arrows.' },
    { ...num('slideWidth', 'Card width', 30, 100), help: 'A share of the box, so under 100 the next card shows at the edge. A phone shows at least 80.' },
    named('arrows', 'Arrows', SLIDER_ARROWS)
  ],
  defaults: {
    props: { slider: 'off', slideWidth: 70, arrows: 'above' },
    styles: onPhone(
      look({
        ...stack(space('sm')), width: '100%', maxWidth: '1440px',
        marginLeft: 'auto', marginRight: 'auto', paddingLeft: '24px', paddingRight: '24px'
      }),
      { paddingLeft: '16px', paddingRight: '16px' }
    )
  },
});
